import React, { useMemo, useState } from 'react';
import {
  IonAlert,
  IonButton,
  IonContent,
  IonHeader,
  IonInput,
  IonItem,
  IonLabel,
  IonList,
  IonModal,
  IonPage,
  IonRefresher,
  IonRefresherContent,
  IonSelect,
  IonSelectOption,
  IonSegment,
  IonSegmentButton,
  IonSpinner,
  IonText,
  IonTextarea,
  IonTitle,
  IonToolbar,
  useIonToast,
} from '@ionic/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CategoryStatus,
  CreateCategoryDto,
  ICategory,
  ICategoryTag,
  UpdateCategoryDto,
} from 'shared';
import apiClient from '../api/client';
import LanguageToggle from '../components/LanguageToggle';
import UserMenu from '../components/UserMenu';
import { useLanguage } from '../context/LanguageContext';
import MetricCard from '../components/MetricCard';
import { RefresherEvent } from '../types/ionic';

interface CategoryFormState {
  id?: string;
  name: string;
  description: string;
  status: CategoryStatus;
  tag?: string;
}

const defaultCategoryForm: CategoryFormState = {
  name: '',
  description: '',
  status: CategoryStatus.ACTIVE,
  tag: undefined,
};

const Categories: React.FC = () => {
  const { t } = useLanguage();
  const [presentToast] = useIonToast();
  const queryClient = useQueryClient();

  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [tagFilter, setTagFilter] = useState<string>('all');
  const [formState, setFormState] = useState<CategoryFormState>(defaultCategoryForm);
  const [showFormModal, setShowFormModal] = useState(false);
  const [mode, setMode] = useState<'create' | 'edit'>('create');
  const [showDeleteAlert, setShowDeleteAlert] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<ICategory | null>(null);

  const categoriesQuery = useQuery({
    queryKey: ['categories'],
    queryFn: async () => {
      const { data } = await apiClient.get<ICategory[]>('/categories');
      return data;
    },
  });

  const tagsQuery = useQuery({
    queryKey: ['category-tags'],
    queryFn: async () => {
      const { data } = await apiClient.get<ICategoryTag[]>('/category-tags');
      return data;
    },
  });

  const createCategory = useMutation({
    mutationFn: async (payload: CreateCategoryDto) => {
      const { data } = await apiClient.post<ICategory>('/categories', payload);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['categories'] });
      presentToast({ message: t('categoryCreated') ?? 'Category created', duration: 2000, color: 'success' });
      closeForm();
    },
    onError: () => {
      presentToast({ message: t('categoryCreateFailed') ?? 'Failed to create category', duration: 2000, color: 'danger' });
    },
  });

  const updateCategory = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdateCategoryDto }) => {
      const { data: response } = await apiClient.put<ICategory>(`/categories/${id}`, data);
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['categories'] });
      presentToast({ message: t('categoryUpdated') ?? 'Category updated', duration: 2000, color: 'success' });
      closeForm();
    },
    onError: () => {
      presentToast({ message: t('categoryUpdateFailed') ?? 'Failed to update category', duration: 2000, color: 'danger' });
    },
  });

  const deleteCategory = useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/categories/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['categories'] });
      presentToast({ message: t('categoryDeleted') ?? 'Category deleted', duration: 2000, color: 'success' });
      setSelectedCategory(null);
    },
    onError: (error: unknown) => {
      const errorMsg =
        (error as { response?: { data?: { message?: string } } })?.response?.data?.message;
      presentToast({
        message: errorMsg || t('categoryDeleteFailed') || 'Failed to delete category',
        duration: 2000,
        color: 'danger',
      });
    },
  });

  const closeForm = () => {
    if (createCategory.isPending || updateCategory.isPending) return;
    setShowFormModal(false);
    setFormState(defaultCategoryForm);
  };

  const openCreateModal = () => {
    setMode('create');
    setFormState(defaultCategoryForm);
    setShowFormModal(true);
  };

  const openEditModal = (category: ICategory) => {
    setMode('edit');
    setFormState({
      id: category.id,
      name: category.name,
      description: category.description ?? '',
      status: category.status,
      tag: category.tag ?? undefined,
    });
    setShowFormModal(true);
  };

  const submitForm = () => {
    if (!formState.name.trim()) {
      presentToast({ message: t('enterCategoryName') ?? 'Enter category name', duration: 2000, color: 'warning' });
      return;
    }
    const payload: CreateCategoryDto = {
      name: formState.name.trim(),
      description: formState.description.trim() || undefined,
      status: formState.status,
      tag: formState.tag || undefined,
    };

    if (mode === 'create') {
      createCategory.mutate(payload);
    } else if (formState.id) {
      updateCategory.mutate({ id: formState.id, data: payload });
    }
  };

  const filteredCategories = useMemo(() => {
    const data = categoriesQuery.data ?? [];
    return data
      .filter((category) => {
        if (statusFilter === 'active') return category.status === CategoryStatus.ACTIVE;
        if (statusFilter === 'inactive') return category.status === CategoryStatus.INACTIVE;
        return true;
      })
      .filter((category) => {
        if (tagFilter === 'all') return true;
        return category.tag === tagFilter;
      });
  }, [categoriesQuery.data, statusFilter, tagFilter]);

  const metrics = useMemo(() => {
    const categories = categoriesQuery.data ?? [];
    const total = categories.length;
    const active = categories.filter((category) => category.status === CategoryStatus.ACTIVE).length;
    const inactive = total - active;
    const tagCounts = (tagsQuery.data ?? []).map((tag) => ({
      tag,
      count: categories.filter((category) => category.tag === tag.slug).length,
    }));
    return { total, active, inactive, tagCounts };
  }, [categoriesQuery.data, tagsQuery.data]);

  const handleRefresh = async (event: RefresherEvent) => {
    try {
      await Promise.all([categoriesQuery.refetch(), tagsQuery.refetch()]);
    } finally {
      event.detail.complete();
    }
  };

  return (
    <IonPage>
      <IonHeader translucent>
        <IonToolbar>
          <IonTitle>{t('categories')}</IonTitle>
          <LanguageToggle />
          <UserMenu />
        </IonToolbar>
      </IonHeader>
      <IonContent fullscreen className="ion-padding">
        <IonRefresher slot="fixed" onIonRefresh={handleRefresh}>
          <IonRefresherContent pullingText="Pull to refresh" />
        </IonRefresher>

        <div style={{ paddingTop: 'var(--ion-safe-area-top)' }}>
          <section style={{ marginBottom: 16 }}>
            <MetricCard
              title={t('totalCategories') ?? 'Total categories'}
              value={`${metrics.total}`}
              context={`${t('active') ?? 'Active'}: ${metrics.active}`}
              accent="primary"
            />
            <MetricCard
              title={t('inactiveCategories') ?? 'Inactive categories'}
              value={`${metrics.inactive}`}
              context={t('categoryStatusSummary') ?? 'Status summary'}
              accent="warning"
            />
          </section>

          <section style={{ marginBottom: 16 }}>
            <IonSegment
              value={statusFilter}
              onIonChange={(event) => {
                const value = (event.detail.value as 'all' | 'active' | 'inactive' | undefined) ?? 'all';
                setStatusFilter(value);
              }}
            >
              <IonSegmentButton value="all">
                <IonLabel>{t('all')}</IonLabel>
              </IonSegmentButton>
              <IonSegmentButton value="active">
                <IonLabel>{t('active')}</IonLabel>
              </IonSegmentButton>
              <IonSegmentButton value="inactive">
                <IonLabel>{t('inactive')}</IonLabel>
              </IonSegmentButton>
            </IonSegment>
            <IonItem style={{ marginTop: 12 }}>
              <IonLabel>{t('categoryTag') ?? 'Tag'}</IonLabel>
              <IonSelect
                interface="popover"
                value={tagFilter}
                onIonChange={(event) => setTagFilter((event.detail.value as string) ?? 'all')}
              >
                <IonSelectOption value="all">{t('all')}</IonSelectOption>
                {(tagsQuery.data ?? []).map((tag) => (
                  <IonSelectOption key={tag.id} value={tag.slug}>
                    {tag.name}
                  </IonSelectOption>
                ))}
              </IonSelect>
            </IonItem>
          </section>

          <IonButton expand="block" onClick={openCreateModal} style={{ marginBottom: 16 }}>
            {t('addCategory') ?? 'Add category'}
          </IonButton>

          {categoriesQuery.isLoading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '48px 0' }}>
              <IonSpinner />
            </div>
          ) : filteredCategories.length === 0 ? (
            <IonText color="medium">
              <p>{t('noData')}</p>
            </IonText>
          ) : (
            <IonList>
              {filteredCategories.map((category) => (
                <IonItem key={category.id} button detail onClick={() => setSelectedCategory(category)}>
                  <IonLabel>
                    <h2 style={{ fontWeight: 600 }}>{category.name}</h2>
                    <p>{category.description || '—'}</p>
                    <p>
                      {t('status')}: {category.status === CategoryStatus.ACTIVE ? t('active') : t('inactive')}
                    </p>
                  </IonLabel>
                </IonItem>
              ))}
            </IonList>
          )}
        </div>

        <IonModal isOpen={showFormModal} onDidDismiss={closeForm}>
          <IonHeader>
            <IonToolbar>
              <IonTitle>{mode === 'create' ? t('addCategory') ?? 'Add category' : t('editCategory') ?? 'Edit category'}</IonTitle>
              <IonButton slot="end" fill="clear" onClick={closeForm}>
                {t('close')}
              </IonButton>
            </IonToolbar>
          </IonHeader>
          <IonContent className="ion-padding">
            <IonList>
              <IonItem>
                <IonLabel position="stacked">{t('categoryName') ?? 'Category name'}</IonLabel>
                <IonInput
                  value={formState.name}
                  onIonChange={(event) => setFormState((prev) => ({ ...prev, name: event.detail.value ?? '' }))}
                />
              </IonItem>
              <IonItem>
                <IonLabel position="stacked">{t('description')}</IonLabel>
                <IonTextarea
                  value={formState.description}
                  onIonChange={(event) => setFormState((prev) => ({ ...prev, description: event.detail.value ?? '' }))}
                  autoGrow
                />
              </IonItem>
              <IonItem>
                <IonLabel position="stacked">{t('status')}</IonLabel>
                <IonSelect
                  value={formState.status}
                  onIonChange={(event) => setFormState((prev) => ({ ...prev, status: (event.detail.value as CategoryStatus) ?? CategoryStatus.ACTIVE }))}
                >
                  <IonSelectOption value={CategoryStatus.ACTIVE}>{t('active')}</IonSelectOption>
                  <IonSelectOption value={CategoryStatus.INACTIVE}>{t('inactive')}</IonSelectOption>
                </IonSelect>
              </IonItem>
              <IonItem>
                <IonLabel position="stacked">{t('categoryTag') ?? 'Tag'}</IonLabel>
                <IonSelect
                  value={formState.tag ?? 'none'}
                  onIonChange={(event) => {
                    const value = event.detail.value as string;
                    setFormState((prev) => ({ ...prev, tag: value === 'none' ? undefined : value }));
                  }}
                >
                  <IonSelectOption value="none">{t('none') ?? 'None'}</IonSelectOption>
                  {(tagsQuery.data ?? []).map((tag) => (
                    <IonSelectOption key={tag.id} value={tag.slug}>
                      {tag.name}
                    </IonSelectOption>
                  ))}
                </IonSelect>
              </IonItem>
            </IonList>
            <IonButton expand="block" style={{ marginTop: 16 }} onClick={submitForm}>
              {mode === 'create' ? t('save') : t('update')}
            </IonButton>
          </IonContent>
        </IonModal>

        <IonModal isOpen={Boolean(selectedCategory)} onDidDismiss={() => setSelectedCategory(null)}>
          <IonHeader>
            <IonToolbar>
              <IonTitle>{selectedCategory?.name ?? t('categories')}</IonTitle>
              <IonButton slot="end" fill="clear" onClick={() => setSelectedCategory(null)}>
                {t('close')}
              </IonButton>
            </IonToolbar>
          </IonHeader>
          <IonContent className="ion-padding">
            {selectedCategory ? (
              <>
                <IonText color="medium">
                  {selectedCategory.description ? <p>{selectedCategory.description}</p> : null}
                  <p>
                    {t('status')}: {selectedCategory.status === CategoryStatus.ACTIVE ? t('active') : t('inactive')}
                  </p>
                  {selectedCategory.tag ? <p>{t('categoryTag') ?? 'Tag'}: {selectedCategory.tag}</p> : null}
                </IonText>
                <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
                  <IonButton expand="block" onClick={() => openEditModal(selectedCategory)}>
                    {t('edit')}
                  </IonButton>
                  <IonButton expand="block" color="danger" onClick={() => setShowDeleteAlert(true)}>
                    {t('delete')}
                  </IonButton>
                </div>
              </>
            ) : null}
          </IonContent>
        </IonModal>

        <IonAlert
          isOpen={showDeleteAlert}
          header={t('confirmDeleteCategory') ?? 'Delete category?'}
          message={t('deleteCategoryMessage') ?? 'This action cannot be undone.'}
          buttons={[
            {
              text: t('cancel'),
              role: 'cancel',
              handler: () => setShowDeleteAlert(false),
            },
            {
              text: t('delete'),
              role: 'destructive',
              handler: () => {
                if (selectedCategory) {
                  deleteCategory.mutate(selectedCategory.id);
                }
                setShowDeleteAlert(false);
              },
            },
          ]}
          onDidDismiss={() => setShowDeleteAlert(false)}
        />
      </IonContent>
    </IonPage>
  );
};

export default Categories;
